export type MusicScreen = "title" | "leaderboard" | "game";

export type MusicPlaybackState = {
  isBetaGateVisible: boolean;
  isDocumentVisible: boolean;
  isTutorialRoundActive: boolean;
  musicVolumePercent: number;
  screen: MusicScreen;
};

export type MusicController = {
  syncPlayback: (state: MusicPlaybackState) => void;
  activatePlayback: (state: MusicPlaybackState) => void;
  dispose: () => void;
};

type AudioContextConstructor = typeof AudioContext;

function getElementVolume(musicVolumePercent: number): number {
  return Math.min(1, Math.max(0, musicVolumePercent / 100));
}

function getAudioContextConstructor(): AudioContextConstructor | null {
  if (typeof window === "undefined") {
    return null;
  }

  const extendedWindow = window as Window & {
    webkitAudioContext?: AudioContextConstructor;
  };

  return window.AudioContext ?? extendedWindow.webkitAudioContext ?? null;
}

function attemptPlay(audioElement: HTMLAudioElement, audioContext: AudioContext | null): void {
  const resumePromise =
    audioContext !== null && audioContext.state === "suspended"
      ? audioContext.resume().catch(() => {
          // Ignore resume failures so the app can retry on the next gesture.
        })
      : Promise.resolve();

  void resumePromise.finally(() => {
    const playPromise = audioElement.play();
    if (playPromise !== undefined) {
      void playPromise.catch(() => {
        // Ignore autoplay rejections so the app can retry on the next gesture.
      });
    }
  });
}

function applyGain(gainNode: GainNode, audioContext: AudioContext, volume: number): void {
  const now = audioContext.currentTime;
  gainNode.gain.cancelScheduledValues(now);
  gainNode.gain.setValueAtTime(gainNode.gain.value, now);
  gainNode.gain.linearRampToValueAtTime(volume, now + 0.01);
}

export function shouldMusicBeActive(state: MusicPlaybackState): boolean {
  return (
    !state.isBetaGateVisible &&
    state.isDocumentVisible &&
    !state.isTutorialRoundActive &&
    state.screen === "game" &&
    state.musicVolumePercent > 0
  );
}

export function createMusicController(trackUrl: string): MusicController {
  let audioElement: HTMLAudioElement | null = null;
  let audioContext: AudioContext | null = null;
  let gainNode: GainNode | null = null;
  let sourceNode: MediaElementAudioSourceNode | null = null;

  const ensureAudioElement = (): HTMLAudioElement | null => {
    if (audioElement !== null) {
      return audioElement;
    }

    if (typeof Audio === "undefined") {
      return null;
    }

    const nextAudioElement = new Audio(trackUrl);
    nextAudioElement.loop = true;
    nextAudioElement.preload = "auto";
    nextAudioElement.volume = 0;
    audioElement = nextAudioElement;
    return audioElement;
  };

  const ensureAudioGraph = (): {
    audioContext: AudioContext | null;
    audioElement: HTMLAudioElement | null;
    gainNode: GainNode | null;
  } => {
    const nextAudioElement = ensureAudioElement();
    if (nextAudioElement === null) {
      return {
        audioContext: null,
        audioElement: null,
        gainNode: null
      };
    }

    if (gainNode !== null) {
      return {
        audioContext,
        audioElement: nextAudioElement,
        gainNode
      };
    }

    const AudioContextCtor = getAudioContextConstructor();
    if (AudioContextCtor === null) {
      return {
        audioContext: null,
        audioElement: nextAudioElement,
        gainNode: null
      };
    }

    try {
      audioContext = new AudioContextCtor();
      sourceNode = audioContext.createMediaElementSource(nextAudioElement);
      gainNode = audioContext.createGain();
      sourceNode.connect(gainNode);
      gainNode.connect(audioContext.destination);
      nextAudioElement.volume = 1;
    } catch {
      audioContext = null;
      sourceNode = null;
      gainNode = null;
    }

    return {
      audioContext,
      audioElement: nextAudioElement,
      gainNode
    };
  };

  const pause = () => {
    if (audioElement === null) {
      return;
    }

    audioElement.pause();
  };

  const applyInactiveState = (musicVolumePercent: number) => {
    if (audioElement === null) {
      return;
    }

    const nextVolume = getElementVolume(musicVolumePercent);
    if (gainNode !== null && audioContext !== null) {
      applyGain(gainNode, audioContext, nextVolume);
    } else {
      audioElement.volume = nextVolume;
    }
    pause();
  };

  const applyActiveState = (musicVolumePercent: number) => {
    const { audioContext: nextAudioContext, audioElement: nextAudioElement, gainNode: nextGainNode } =
      ensureAudioGraph();
    if (nextAudioElement === null) {
      return;
    }

    const nextVolume = getElementVolume(musicVolumePercent);
    if (nextGainNode !== null && nextAudioContext !== null) {
      applyGain(nextGainNode, nextAudioContext, nextVolume);
      nextAudioElement.volume = 1;
    } else {
      nextAudioElement.volume = nextVolume;
    }

    attemptPlay(nextAudioElement, nextAudioContext);
  };

  return {
    syncPlayback: (state) => {
      if (!shouldMusicBeActive(state)) {
        applyInactiveState(state.musicVolumePercent);
        return;
      }

      applyActiveState(state.musicVolumePercent);
    },
    activatePlayback: (state) => {
      if (!shouldMusicBeActive(state)) {
        return;
      }

      applyActiveState(state.musicVolumePercent);
    },
    dispose: () => {
      if (audioElement === null) {
        return;
      }

      audioElement.pause();
      audioElement.src = "";
      audioElement.load();
      sourceNode?.disconnect();
      gainNode?.disconnect();
      if (audioContext !== null && audioContext.state !== "closed") {
        void audioContext.close().catch(() => {
          // Ignore close failures during teardown.
        });
      }
      sourceNode = null;
      gainNode = null;
      audioContext = null;
      audioElement = null;
    }
  };
}
